import { ServicePageTemplate } from "@/components/ServicePageTemplate";
import { Megaphone, Target, Filter, BarChart3, Smartphone, Zap, PenTool, Layout, MessageSquare, Globe, MapPin, Cpu } from "lucide-react";
import { RoiCalculator } from "@/components/RoiCalculator";

const features = [
  {
    icon: Target,
    title: "Ad Strategy & Campaign Planning",
    description: "Custom campaign strategy tailored to your trade, target suburbs, and ideal job types for maximum ROI.",
  },
  {
    icon: Filter,
    title: "Lead Qualification",
    description: "Every enquiry is pre-qualified by suburb, job type, and intent — so you only speak to serious, ready-to-book customers.",
  },
  {
    icon: Megaphone,
    title: "Facebook & Instagram Ad Campaigns",
    description: "Scroll-stopping social media campaigns that put your business in front of homeowners actively looking for your services.",
  },
  {
    icon: Zap,
    title: "Google Ads Campaigns",
    description: "High-intent Google Search and Maps campaigns targeting customers actively searching for your trade right now.",
  },
  {
    icon: PenTool,
    title: "Ad Copywriting & Creative Design",
    description: "Professionally written ad copy and designed creatives that grab attention and drive quality enquiries.",
  },
  {
    icon: Layout,
    title: "Lead Form & Landing Page Setup",
    description: "Conversion-optimised lead forms and landing pages designed to capture enquiries and maximise your ad spend.",
  },
  {
    icon: Smartphone,
    title: "Instant SMS Response",
    description: "New enquiries receive an instant SMS response so leads stay warm and engaged before competitors can reach them.",
  },
  {
    icon: MessageSquare,
    title: "CRM Lead Tracking & Pipeline Management",
    description: "Every lead tracked in your CRM with full pipeline visibility so nothing falls through the cracks.",
  },
  {
    icon: BarChart3,
    title: "Ongoing Optimisation & Monitoring",
    description: "Continuous campaign optimisation and performance monitoring to improve results and lower your cost per lead over time.",
  },
];

const benefits = [
  "Predictable lead flow every single week",
  "Quality leads pre-qualified and ready to book",
  "Suburb + job-type precision targeting",
  "Full transparency on ad spend and ROI",
  "Less downtime between jobs",
  "Pre-qualified leads — no tyre-kickers",
  "Instant SMS follow-up keeps leads warm",
  "Monthly campaign optimisation for better results",
];

export default function LeadGeneration() {
  return (
    <ServicePageTemplate
      title="Lead Generation — Google & Facebook Ads"
      subtitle="Targeted ad campaigns that bring pre-qualified leads ready to book — not tyre-kickers."
      metaTitle="Lead Generation Ads for Tradies | FullScale Marketing"
      metaDescription="Targeted Google Ads and Facebook Ads for Australian tradies. Quality leads, precision targeting, and pre-qualified enquiries that convert."
      canonicalUrl="https://fullscalemarketing.com.au/services/lead-generation"
      badgeText="Lead Generation"
      icon={Megaphone}
      introText="Stop wasting money on ads that bring tyre-kickers. Our lead generation system uses precision targeting, pre-qualification, and instant follow-up to deliver quality leads that are ready to book — in your target suburbs, for the jobs you actually want. For best results, pair lead generation with a SMART website and automation tools to capture and convert every enquiry."
      features={features}
      benefits={benefits}
      ctaHeading="Ready for a Predictable Stream of Quality Leads?"
      ctaText="Book a free strategy call and we'll show you how targeted ads can fill your pipeline with pre-qualified leads every week."
      extraContent={<RoiCalculator />}
      relatedServices={[
        {
          icon: Globe,
          title: "SMART Websites",
          description: "Landing pages and websites that convert ad traffic into booked jobs at higher rates.",
          href: "/services/websites",
        },
        {
          icon: MapPin,
          title: "Google Business Profile & SEO",
          description: "Combine paid ads with organic local SEO for maximum visibility in your area.",
          href: "/services/gbp-seo",
        },
        {
          icon: Cpu,
          title: "Automation & AI Tools",
          description: "Instant follow-up and CRM automation ensure no lead slips through the cracks.",
          href: "/services/automation",
        },
      ]}
      faqs={[
        {
          question: "How much should I spend on ads?",
          answer: "It depends on your trade, location, and goals. Most tradies start seeing strong results from $1,500–$3,000/month in ad spend. We'll recommend a budget based on your target suburbs and job types during your strategy call.",
        },
        {
          question: "How quickly will I start getting leads?",
          answer: "Most campaigns start generating leads within the first 1–2 weeks. Google Ads can deliver leads almost immediately, while Facebook campaigns typically need 2–3 weeks to fully optimise.",
        },
        {
          question: "What's the difference between Google Ads and Facebook Ads?",
          answer: "Google Ads target people actively searching for your services right now — high intent. Facebook & Instagram Ads put your business in front of homeowners before they search — great for awareness and generating demand. We recommend both for maximum lead flow.",
        },
        {
          question: "How do you make sure leads are quality, not tyre-kickers?",
          answer: "We use suburb targeting, job-type filters, and pre-qualification questions in our lead forms. Every enquiry is filtered by location, intent, and service type before it reaches you.",
        },
        {
          question: "Do I need a website to run ads?",
          answer: "Not necessarily. We can run campaigns using lead forms directly on Facebook and Google. However, having a SMART Website significantly improves conversion rates and gives you a professional presence that builds trust.",
        },
      ]}
    />
  );
}
